import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Download, Plus, Trash2, ExternalLink, Play, FileText, BookOpen, Folder, Loader2, Pencil, Save, X, Link as LinkIcon, Upload, FileVideo } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const CATEGORIES = [
  { id: "video", label: "Vídeo", Icon: Play, cls: "bg-sky-100 text-sky-700" },
  { id: "tutorial", label: "Tutorial", Icon: BookOpen, cls: "bg-emerald-100 text-emerald-700" },
  { id: "manual", label: "Manual", Icon: FileText, cls: "bg-amber-100 text-amber-700" },
  { id: "otros", label: "Otros", Icon: Folder, cls: "bg-slate-100 text-slate-700" },
];

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const isInternalDownload = (url) => typeof url === "string" && url.startsWith("/api/downloads/file/");

// Cualquier URL que empiece por "/" es un archivo local servido por nuestro dominio
const isLocalFile = (url) => typeof url === "string" && url.startsWith("/");

const buildDownloadHref = (url) => {
  if (!isInternalDownload(url)) return url;
  // url empieza por "/api/downloads/file/..." — SEC-002: NUNCA incluir el token
  // de sesión en la URL (quedaría en logs/historial). La descarga se hace vía
  // fetch con header Authorization (forceDownload).
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${url}`;
};

// Descarga forzada vía blob: la única forma 100% fiable en todos los navegadores.
// Necesaria porque algunos navegadores ignoran el atributo `download` y abren PDFs/MP4s
// en la pestaña en vez de descargarlos.
async function forceDownload(url, filename) {
  try {
    const token = localStorage.getItem("fc_token") || "";
    const resp = await fetch(url, {
      credentials: "omit",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename || "descarga";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  } catch (e) {
    // Fallback: abrir en pestaña (mejor que no hacer nada)
    window.open(url, "_blank", "noopener,noreferrer");
    toast.error("No se pudo descargar directamente; se ha abierto en una pestaña nueva. " + (e?.message || ""));
  }
}

const getYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};
const getVimeoId = (url) => {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
};

function DownloadThumbnail({ url, thumbnail, mime }) {
  const ytId = getYouTubeId(url);
  const vmId = getVimeoId(url);
  const src = thumbnail || (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null);
  if (src) {
    return (
      <div className="relative aspect-video bg-slate-200 rounded-lg overflow-hidden">
        <img src={src} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 grid place-items-center bg-black/0 group-hover:bg-black/30 transition-colors">
          <div className="h-12 w-12 rounded-full bg-white/95 grid place-items-center scale-90 group-hover:scale-100 transition-transform">
            <Play className="h-5 w-5 text-slate-900 ml-0.5" fill="currentColor" />
          </div>
        </div>
      </div>
    );
  }
  // Sin miniatura: elegir icono según tipo
  const isVideo = (mime || "").startsWith("video/");
  const isAudio = (mime || "").startsWith("audio/");
  const isPdf = (mime || "").includes("pdf");
  const Icon = isVideo ? Play : isAudio ? Play : isPdf ? FileText : FileText;
  const grad = isVideo
    ? "from-sky-100 to-sky-200 text-sky-500"
    : isAudio
    ? "from-purple-100 to-purple-200 text-purple-500"
    : isPdf
    ? "from-rose-100 to-rose-200 text-rose-500"
    : "from-slate-100 to-slate-200 text-slate-400";
  return (
    <div className={`aspect-video bg-gradient-to-br ${grad} rounded-lg grid place-items-center`}>
      <Icon className="h-10 w-10" />
    </div>
  );
}

function DownloadForm({ initial, onSubmit, onUpload, onCancel, submitting }) {
  const isEdit = !!initial?.id;
  const initialMode = isEdit && initial?.uploaded_file ? "file" : "url";
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(initial || { title: "", description: "", category: "video", url: "", thumbnail: "", order: 0 });
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    // Modo archivo (sólo cuando es nuevo recurso)
    if (mode === "file" && !isEdit) {
      if (!file) {
        toast.error("Selecciona un archivo");
        return;
      }
      await onUpload({ ...form, file, onProgress: setUploadProgress });
      return;
    }
    // Modo URL externa
    if (!form.url.trim()) {
      toast.error("La URL es obligatoria");
      return;
    }
    onSubmit({ ...form, order: Number(form.order) || 0 });
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {!isEdit && (
        <div className="flex gap-2" role="tablist">
          <button
            type="button"
            onClick={() => setMode("url")}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-sm font-bold border transition-colors ${mode === "url" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
            data-testid="dl-mode-url"
          >
            <LinkIcon className="h-4 w-4" /> Enlace externo (YouTube / web)
          </button>
          <button
            type="button"
            onClick={() => setMode("file")}
            className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-sm font-bold border transition-colors ${mode === "file" ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
            data-testid="dl-mode-file"
          >
            <Upload className="h-4 w-4" /> Subir archivo (vídeo, PDF…)
          </button>
        </div>
      )}

      <label className="block">
        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Título*</span>
        <input
          type="text"
          value={form.title}
          onChange={change("title")}
          className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
          data-testid="dl-title"
          required
        />
      </label>

      {mode === "url" ? (
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">URL* (YouTube, Vimeo o enlace directo)</span>
          <input
            type="url"
            value={form.url}
            onChange={change("url")}
            placeholder="https://youtu.be/…"
            className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
            data-testid="dl-url"
            required={mode === "url"}
          />
        </label>
      ) : (
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Archivo* (máx. 500 MB)</span>
          <div className="mt-1 border-2 border-dashed border-slate-300 hover:border-sky-400 rounded-md p-4 text-center transition-colors bg-slate-50">
            <input
              type="file"
              id="dl-file-input"
              accept=".mp4,.mov,.webm,.avi,.mkv,.m4v,.pdf,.mp3,.wav,.m4a,.ogg,.zip,.rar,.7z,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="dl-file-input"
            />
            <label htmlFor="dl-file-input" className="cursor-pointer block">
              {file ? (
                <div className="space-y-1">
                  <FileVideo className="h-8 w-8 mx-auto text-sky-500" />
                  <div className="text-sm font-bold text-slate-900 truncate">{file.name}</div>
                  <div className="text-xs text-slate-500">{fmtSize(file.size)} · {file.type || "tipo desconocido"}</div>
                  <div className="text-[11px] text-sky-700 mt-1">Cambiar archivo</div>
                </div>
              ) : (
                <div className="space-y-1 text-slate-500">
                  <Upload className="h-8 w-8 mx-auto" />
                  <div className="text-sm font-medium">Haz clic o arrastra el archivo aquí</div>
                  <div className="text-[11px]">Vídeos (MP4, MOV…), PDFs, audios, ZIP, Office, imágenes</div>
                </div>
              )}
            </label>
          </div>
          {submitting && mode === "file" && uploadProgress > 0 && uploadProgress < 100 && (
            <div className="mt-2">
              <div className="text-[11px] text-slate-600 mb-1">Subiendo… {uploadProgress}%</div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-sky-500 transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}
        </label>
      )}

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Categoría</span>
          <select
            value={form.category}
            onChange={change("category")}
            className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-200"
            data-testid="dl-category"
          >
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Orden</span>
          <input
            type="number"
            value={form.order}
            onChange={change("order")}
            className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
            data-testid="dl-order"
          />
        </label>
      </div>

      {mode === "url" && (
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Miniatura (opcional, URL)</span>
          <input
            type="url"
            value={form.thumbnail || ""}
            onChange={change("thumbnail")}
            placeholder="Si vacío, se usa la portada de YouTube"
            className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
            data-testid="dl-thumbnail"
          />
        </label>
      )}
      <label className="block">
        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Descripción (opcional)</span>
        <textarea
          rows={3}
          value={form.description || ""}
          onChange={change("description")}
          className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
          data-testid="dl-description"
        />
      </label>
      <DialogFooter className="!justify-between gap-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="dl-cancel">
          Cancelar
        </button>
        <button
          type="submit"
          disabled={submitting}
          className={`inline-flex items-center gap-2 ${mode === "file" ? "bg-sky-600 hover:bg-sky-700" : "bg-slate-900 hover:bg-slate-800"} disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-full`}
          data-testid="dl-submit"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "file" ? <Upload className="h-4 w-4" /> : <Save className="h-4 w-4" />}
          {mode === "file" && !isEdit ? "Subir archivo" : "Guardar"}
        </button>
      </DialogFooter>
    </form>
  );
}

export default function DownloadsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // {} for new, {id,...} for edit, null closed
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/downloads");
      setItems(data?.downloads || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar las descargas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((i) => i.category === filter);
  }, [items, filter]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    CATEGORIES.forEach((cat) => { c[cat.id] = items.filter((i) => i.category === cat.id).length; });
    return c;
  }, [items]);

  const save = async (form) => {
    setSubmitting(true);
    try {
      if (editing?.id) {
        await api.patch(`/admin/downloads/${editing.id}`, form);
        toast.success("Descarga actualizada");
      } else {
        await api.post("/admin/downloads", form);
        toast.success("Descarga añadida");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const upload = async ({ title, description, category, order, file, onProgress }) => {
    setSubmitting(true);
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB — pasa cómodo por ingresses habituales
    let uploadId = null;
    try {
      // 1) INIT
      const initFd = new FormData();
      initFd.append("filename", file.name);
      initFd.append("total_size", String(file.size));
      const initR = await api.post("/admin/downloads/upload/init", initFd);
      uploadId = initR.data.upload_id;

      // 2) CHUNK loop
      const total = file.size;
      let sent = 0;
      let chunkIndex = 0;
      for (let start = 0; start < total; start += CHUNK_SIZE) {
        const end = Math.min(total, start + CHUNK_SIZE);
        const blob = file.slice(start, end);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", String(chunkIndex));
        fd.append("chunk", blob, `chunk_${chunkIndex}`);
        await api.post("/admin/downloads/upload/chunk", fd);
        sent = end;
        chunkIndex += 1;
        if (onProgress) onProgress(Math.round((sent / total) * 100));
      }

      // 3) COMPLETE
      const doneFd = new FormData();
      doneFd.append("upload_id", uploadId);
      doneFd.append("title", title);
      doneFd.append("description", description || "");
      doneFd.append("category", category || "otros");
      doneFd.append("order", String(Number(order) || 0));
      await api.post("/admin/downloads/upload/complete", doneFd);

      toast.success("Archivo subido y publicado");
      setEditing(null);
      await load();
    } catch (e) {
      // Best-effort cleanup si la sesión se creó pero falló en medio
      if (uploadId) {
        try { await api.delete(`/admin/downloads/upload/${uploadId}`); } catch (_) { /* best-effort */ }
      }
      toast.error(e?.response?.data?.detail || "Error al subir el archivo");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar esta descarga?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/downloads/${id}`);
      toast.success("Eliminada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    } finally {
      setBusyId(null);
    }
  };

  const checkOrphans = async () => {
    try {
      const { data } = await api.get("/admin/downloads/orphans");
      const n = data?.total || 0;
      if (n === 0) {
        toast.success("No hay archivos huérfanos. Todo OK.");
        return;
      }
      const list = (data.orphans || []).map((o) => `• ${o.title || o.original_filename || o.id}`).join("\n");
      if (!confirm(`Se han detectado ${n} archivo(s) huérfano(s) (subidos antes y perdidos tras un redeploy):\n\n${list}\n\n¿Eliminar estos registros de la base de datos? Tendrás que volver a subir los ficheros originales.`)) {
        return;
      }
      const { data: r } = await api.delete("/admin/downloads/orphans");
      toast.success(`Eliminados ${r.deleted} registro(s) huérfano(s).`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error comprobando huérfanos");
    }
  };

  const downloadUserManual = async () => {
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      await forceDownload(`${base}/api/manual/download`, "FarmaClass-Manual-Usuario.pdf");
    } catch (e) {
      toast.error("No se pudo descargar el manual: " + (e?.message || ""));
    }
  };

  const downloadWhatsNew = async () => {
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      await forceDownload(`${base}/api/manual/novedades`, "FarmaClass-Mejoras.pdf");
    } catch (e) {
      toast.error("No se pudo descargar el listado de mejoras: " + (e?.message || ""));
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="downloads-page">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-start">
        <div className="min-w-0">
          {/* Manual de Usuario — CTA destacado */}
          <div
            className="mb-6 rounded-2xl bg-gradient-to-br from-slate-950 to-slate-800 text-white p-6 flex items-center justify-between gap-6 shadow-lg"
            data-testid="manual-cta"
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="h-12 w-12 rounded-xl bg-amber-400/20 grid place-items-center shrink-0">
                <BookOpen className="h-6 w-6 text-amber-300" strokeWidth={1.6} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold mb-0.5">Recomendado</div>
                <div className="font-display text-xl font-bold leading-tight">Manual de Usuario · 49 páginas con capturas</div>
                <div className="text-slate-300 text-sm mt-1 leading-snug">
                  Guía completa paso a paso con pantallas reales de la herramienta: dispensaciones, semáforo, Top 25 «Vs Mercado», moléculas en caída, pedido inteligente, traslados, coaching IA, simulador e incentivos.
                </div>
              </div>
            </div>
            <button
              onClick={downloadUserManual}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-400 text-slate-950 text-sm font-bold hover:bg-amber-300 transition-all shadow-sm whitespace-nowrap"
              data-testid="dl-user-manual"
            >
              <Download className="h-4 w-4" /> Descargar PDF
            </button>
          </div>

          {/* Listado de mejoras */}
          <div
            className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            data-testid="whatsnew-cta"
          >
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-bold mb-0.5">Novedades</div>
              <div className="font-display text-lg font-bold leading-tight text-emerald-950">Listado de mejoras · lo último que hemos añadido</div>
              <div className="text-emerald-800/80 text-sm mt-1 leading-snug">
                Atajos del menú, catálogo con G.H. y corrección de fichas, comparador de vademécum, resumen antes de aplicar condiciones y la pantalla de stock que no vendes.
              </div>
            </div>
            <button
              onClick={downloadWhatsNew}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800 transition-all whitespace-nowrap"
              data-testid="dl-whats-new"
            >
              <Download className="h-4 w-4" /> Descargar mejoras
            </button>
          </div>

          <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Recursos</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Descargas y formación</h1>
          <p className="text-slate-500 mt-1 text-sm">Vídeos de formación, tutoriales y manuales para sacar el máximo partido a la plataforma.</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              onClick={checkOrphans}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
              data-testid="dl-cleanup-orphans"
              title="Detectar y eliminar registros cuyos archivos se han perdido tras un redeploy"
            >
              <Trash2 className="h-4 w-4" /> Limpiar huérfanos
            </button>
            <button
              onClick={() => setEditing({})}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
              data-testid="dl-new"
            >
              <Plus className="h-4 w-4" /> Añadir recurso
            </button>
          </div>
        )}
      </header>

      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setFilter("all")}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter === "all" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 hover:bg-slate-50"}`}
          data-testid="dl-filter-all"
        >
          Todos · {counts.all || 0}
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setFilter(c.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 ${filter === c.id ? "bg-slate-900 text-white" : "bg-white border border-slate-200 hover:bg-slate-50"}`}
            data-testid={`dl-filter-${c.id}`}
          >
            <c.Icon className="h-3 w-3" /> {c.label} · {counts[c.id] || 0}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center" data-testid="dl-empty">
          <Download className="h-8 w-8 mx-auto text-slate-300 mb-3" />
          <div className="font-display text-xl font-bold mb-1">Sin recursos por ahora</div>
          <div className="text-slate-500 text-sm">
            {isAdmin ? "Pulsa \"Añadir recurso\" para empezar." : "Pronto añadiremos vídeos y tutoriales aquí."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="dl-list">
          {filtered.map((d) => {
            const cat = CATEGORIES.find((c) => c.id === d.category) || CATEGORIES[3];
            const isInternal = isInternalDownload(d.url);
            const isLocal = isLocalFile(d.url);
            const shouldForce = isInternal || isLocal;
            const href = buildDownloadHref(d.url);
            const dlName = d.original_filename || (isLocal ? d.url.split("/").pop() : undefined);
            const handleDownload = (e) => {
              if (!shouldForce) return; // URL externa → comportamiento por defecto del <a>
              e.preventDefault();
              forceDownload(href, dlName);
            };
            return (
              <div key={d.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col group" data-testid={`dl-${d.id}`}>
                <a
                  href={shouldForce ? "#" : href}
                  target={shouldForce ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  onClick={handleDownload}
                  className="block cursor-pointer"
                >
                  <DownloadThumbnail url={d.url} thumbnail={d.thumbnail} mime={d.mime_type} />
                </a>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cat.cls}`}>
                      <cat.Icon className="h-3 w-3" /> {cat.label}
                    </span>
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => setEditing(d)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" title="Editar" data-testid={`dl-edit-${d.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => remove(d.id)} disabled={busyId === d.id} className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50" title="Eliminar" data-testid={`dl-remove-${d.id}`}>
                          {busyId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="font-display text-base font-bold leading-tight line-clamp-2">{d.title}</div>
                  {d.description && <p className="text-xs text-slate-500 mt-1 line-clamp-3 flex-1">{d.description}</p>}
                  {isInternal && d.file_size > 0 && (
                    <div className="text-[10px] text-slate-400 mt-1 font-mono-num">
                      {d.original_filename || ""} · {fmtSize(d.file_size)}
                    </div>
                  )}
                  {shouldForce ? (
                    <button
                      type="button"
                      onClick={() => forceDownload(href, dlName)}
                      className="mt-3 inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-4 py-2 rounded-full"
                      data-testid={`dl-open-${d.id}`}
                    >
                      <Download className="h-3 w-3" /> Descargar
                    </button>
                  ) : (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-4 py-2 rounded-full"
                      data-testid={`dl-open-${d.id}`}
                    >
                      Abrir <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isAdmin && (
        <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) setEditing(null); }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2">
                {editing?.id ? <><Pencil className="h-5 w-5 text-sky-500" /> Editar recurso</> : <><Plus className="h-5 w-5 text-sky-500" /> Nuevo recurso</>}
              </DialogTitle>
              <DialogDescription>
                Pega un enlace a YouTube, Vimeo o cualquier URL pública. Los usuarios podrán abrirlo directamente.
              </DialogDescription>
            </DialogHeader>
            {editing !== null && (
              <DownloadForm
                initial={editing?.id ? editing : null}
                onSubmit={save}
                onUpload={upload}
                onCancel={() => setEditing(null)}
                submitting={submitting}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
        </div>

        {/* Sidebar lateral · branding Archiduque Consulting */}
        <aside className="hidden lg:block lg:sticky lg:top-8" data-testid="downloads-sidebar">
          <div className="rounded-2xl overflow-hidden bg-slate-950 text-white shadow-lg">
            <div className="relative">
              <img
                src="/img/archiduque-consulting.jpg"
                alt="Archiduque Consulting"
                className="w-full h-auto object-cover block"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/10 to-transparent" />
            </div>
            <div className="px-5 py-5">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-sky-300/80 mb-1">Consultoría especializada</div>
              <div className="font-display text-lg font-extrabold leading-tight">Tu farmacia, más rentable.</div>
              <p className="text-xs text-white/70 mt-2 leading-relaxed">
                Más de 15 años acompañando a oficinas de farmacia en optimización de compras, márgenes y dispensación genérica.
              </p>
              <a
                href="https://archiduquecarlos.com"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-sky-300 hover:text-sky-200"
                data-testid="archiduque-cta"
              >
                Conoce Archiduque Consulting <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
          <div className="mt-4 text-[11px] text-slate-400 text-center">
            ¿Necesitas algo concreto? Escríbenos a <a href="mailto:pedro@archiduquecarlos.com" className="text-slate-600 hover:text-slate-900 underline">pedro@archiduquecarlos.com</a>
          </div>
        </aside>
      </div>
    </div>
  );
}
